import React, { useState, useEffect } from 'react';
import { useTheme } from '@emotion/react';
import { styled } from '@mui/styles';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import BreadcrumbHeader from 'layout/BreadcrumbsHeader';
import ThemeBGWrapper from 'ui-components/ThemeBGWrapper';
import CustomLabel from 'ui-components/CustomLabel';
import GeoMultiSelect from 'ui-components/GeoMultiSelect';
import CustomInputField from 'ui-components/CustomInputField';
import { WHITEBG } from 'ui-components/CustomButton';
import { Primary, Secondary } from 'ui-components/Buttons';
import GradientIcon from 'ui-components/icons/GradientIcon';
import { Check, Upload } from 'lucide-react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { containerRef, categoryIdsToNames } from 'utils';
import { useToast } from 'context/ToastContext';
import { useProcessingSnackbar } from 'context/ProcessingSnackbarContext';
import { CreateAnalysisBatch } from 'api/bulk-analysis';
import { GetInvestorSetting, UpdateInvestorSetting } from 'api/investors';
import BulkAnalysisStartSkeleton from '../skeletons/BulkAnalysisStartSkeleton';
import { FetchAllBusinessModel, FetchAllCategories, FetchAllStages, FetchAllSubCategories } from 'api/utils';
import {
  InfoOutlined as InfoOutlinedIcon,
  AutoFixHigh as SparkleIcon,
  FileUpload as UploadIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import AssessmentFooter from '../components/AssessmentFooter';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const STORAGE_KEY = 'bulk_analysis_state';
const FILTER_STORAGE_KEY = 'bulk_analysis_filters';
const STEPS = ['Select Pitch Decks', 'Investment Thesis'];

const BRAND_GRADIENT = 'linear-gradient(135deg, #7852F8 0%, #84AEF6 100%)';

// The gradient-border recipe from WHITEBG (CustomButton.js:348-355): a white fill
// layer over the brand gradient, clipped so only the border shows the gradient.
// It sets backgroundImage, so it stands in for backgroundColor.
const GRADIENT_BORDER_BG = `linear-gradient(#FFFFFF, #FFFFFF), ${BRAND_GRADIENT}`;

const BulkAnalysisStartPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const c = theme.palette.customColors;
  const investorDetails = useSelector((state) => state?.investorDetails?.data);
  const investor_id = investorDetails?._id;

  // ── wizard step (0 = upload, 1 = thesis) ──────────────────────────────
  const [wizardStep, setWizardStep] = useState(0);

  // ── restore persisted processing state ────────────────────────────────
  const getInitialState = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved?.batchId && saved?.investorId === investor_id && saved?.step === 'processing') {
        return { step: 'processing', batchId: saved.batchId, uploadedFileNames: saved.uploadedFileNames || [] };
      }
    } catch (_) { }
    return { step: 'idle', batchId: null, uploadedFileNames: [] };
  };

  const initialState = getInitialState();
  const [uploadStep, setUploadStep] = useState(initialState.step);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const { addBatch, clearCompletedBatches } = useProcessingSnackbar();

  const [shortlistConfig, setShortlistConfig] = useState({
    category: '', selectedCategories: '', sector: '', stage: '', business_model: '', country: '', state: '', city: '', startup_criteria: '',
  });

  // ── API hooks ──────────────────────────────────────────────────────────
  const { mutateAsync: uploadBatch, isLoading: isUploading } = CreateAnalysisBatch();
  const { mutateAsync: updateSettingAsync } = UpdateInvestorSetting();
  const { data: investorSetting } = GetInvestorSetting({ investor_id, module: 'deal_flow' });
  const { data: categories = [], isLoading: isCategoriesLoading } = FetchAllCategories();
  const { data: stages = [], isLoading: isStagesLoading } = FetchAllStages();
  const { data: subCategories = [] } = FetchAllSubCategories({ category: shortlistConfig?.selectedCategories?.split(',') || '' });
  const { data: businessModels = [], isLoading: isBusinessModelsLoading } = FetchAllBusinessModel();

  const isDataLoading = isCategoriesLoading || isStagesLoading || isBusinessModelsLoading;

  // ── effects ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isUploading) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isUploading]);

  useEffect(() => {
    if (initialState.step === 'processing' && initialState.batchId) {
      addBatch(initialState.batchId, {
        batchType: 'bulk_analysis',
        uploadStep: initialState.step,
        uploadedFileNames: initialState.uploadedFileNames,
        notifyOnComplete: false,
      });
      setUploadStep('processing');
    }
  }, []);

  // Load thesis from backend or localStorage. Waits for `categories` — selectedCategories
  // (names) drives the sector fetch and has to be derived from the saved category ids when
  // the thesis didn't store it.
  useEffect(() => {
    if (!categories?.length) return;
    try {
      const thesis = investorSetting?.investment_thesis;
      if (thesis) {
        setConfig({
          stage: thesis.stage || '',
          category: thesis.category || '',
          selectedCategories: thesis.selectedCategories || categoryIdsToNames(thesis.category, categories),
          sector: thesis.sector || '',
          business_model: thesis.business_model || '',
          startup_criteria: thesis.startup_criteria || '',
        });
      } else {
        // Fallback to localStorage if no backend thesis
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (saved?.thesis && saved?.investorId === investor_id) {
          setConfig({
            stage: saved.thesis.stage || '',
            category: saved.thesis.category || '',
            selectedCategories: saved.thesis.selectedCategories || categoryIdsToNames(saved.thesis.category, categories),
            sector: saved.thesis.sector || '',
            business_model: saved.thesis.business_model || '',
            startup_criteria: saved.thesis.startup_criteria || '',
          });
        }
      }
    } catch (_) { }
  }, [investorSetting, investor_id, categories]);

  // ── handlers ───────────────────────────────────────────────────────────
  const handleFileSelect = (files) => {
    const newFiles = Array.from(files);
    const errors = [];
    newFiles.forEach((file) => {
      if (file.type !== 'application/pdf') errors.push(`"${file.name}" is not a PDF`);
      else if (file.size > 50 * 1024 * 1024) errors.push(`"${file.name}" exceeds the 50MB limit`);
    });
    let validNew = newFiles.filter((f) => f.type === 'application/pdf' && f.size <= 50 * 1024 * 1024);
    const remaining = 50 - selectedFiles.length;
    if (validNew.length > remaining) {
      errors.push(`Only ${remaining} more file${remaining !== 1 ? 's' : ''} can be added (max 50). ${validNew.length - remaining} skipped.`);
      validNew = validNew.slice(0, remaining);
    }
    errors.forEach((e) => addToast(e, 'error'));
    if (validNew.length > 0) setSelectedFiles((prev) => [...prev, ...validNew]);
  };

  const reset = () => {
    setWizardStep(0);
    setUploadStep('idle');
    setSelectedFiles([]);
    const thesis = shortlistConfig.startup_criteria;
    setShortlistConfig({ category: '', selectedCategories: '', sector: '', stage: '', business_model: '', startup_criteria: thesis });
    clearCompletedBatches();
  };

  const handleUpload = async () => {
    if (!shortlistConfig.stage) {
      addToast('Please select at least one Stage', 'error');
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((f) => formData.append('files', f));
    formData.append('user_profile_id', investor_id);
    formData.append('stage', shortlistConfig.stage);
    if (shortlistConfig.category) formData.append('category', shortlistConfig.category);
    if (shortlistConfig.business_model) formData.append('business_model', shortlistConfig.business_model);
    if (shortlistConfig.country) formData.append('country', shortlistConfig.country);
    if (shortlistConfig.state) formData.append('state', shortlistConfig.state);
    if (shortlistConfig.city) formData.append('city', shortlistConfig.city);
    if (shortlistConfig.startup_criteria) formData.append('startup_criteria', shortlistConfig.startup_criteria);

    try {
      const res = await uploadBatch(formData);
      const newBatchId = res?.data?.data?.batch_id;
      const reportName = res?.data?.data?.report_name;
      const fileNames = selectedFiles.map((f) => ({ file_name: f.name, status: 'uploaded' }));

      // Update localStorage immediately with current batch info
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
        "statusFilter": null,
        "selectedReport": {
          "label": reportName || 'Latest Report',
          "batch_id": newBatchId
        }
      }));

      // Add batch to context
      addBatch(newBatchId, {
        batchType: 'bulk_analysis',
        uploadStep: 'processing',
        uploadedFileNames: fileNames,
        notifyOnComplete: false,
      });

      setUploadStep('processing');
      setSelectedFiles([]);

      // Navigate asynchronously without waiting
      setTimeout(() => {
        navigate(`/bulk-analysis?from=start`);
      }, 0);
    } catch (err) {
      addToast(err?.response?.data?.message || 'Upload failed. Please try again.', 'error');
    }
  };

  const setConfig = (patch) => setShortlistConfig((prev) => ({ ...prev, ...patch }));

  const multiSelectProps = (field, options, placeholder) => ({
    multiple: true,
    // disablePortal: true,
    fullWidth: true,
    autoHighlight: true,
    options: options.filter((o) => !shortlistConfig[field].split(',').filter(Boolean).includes(o.value)),
    value: shortlistConfig[field].split(',').filter(Boolean).map((v) => ({
      label: options?.find((item) => item?.value === v)?.label,
      value: v,
    })),
    onChange: (_, newVal) => setConfig({ [field]: newVal.map((o) => o.value).join(',') }),
    renderTags: (selected, getTagProps) =>
      selected.map((opt, i) => <Chip key={opt.value} label={opt.label} {...getTagProps({ index: i })} />),
    renderInput: (params) => (
      <CustomInputField
        variant="outlined"
        placeholder={placeholder}
        {...params}
        sx={{
          marginBottom: 0, "& .MuiOutlinedInput-root .MuiOutlinedInput-input ": {
            padding: "0.5rem"
          }
        }}

        className="filter-autocomplete"
      />
    ),
  });

  const hasFiles = selectedFiles.length > 0;

  // Stepper circles: MUI's StepIcon is fill-based and the sx pointed at c.b300
  // ('black' in this theme). Render the circle ourselves so it carries the brand
  // gradient. Numbers stay — no checkmark on completed.
  const StepCircle = ({ active, completed, icon }) => (
    <Box
      sx={{
        width: { xs: 28, sm: 32 },
        height: { xs: 28, sm: 32 },
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
        lineHeight: 1,
        ...(active || completed
          ? {
            background: 'linear-gradient(135deg, #7852F8 0%, #84AEF6 100%)',
            color: '#FFFFFF',
            fontWeight: 700,
            boxShadow: '0 0 0 4px rgba(120, 82, 248, 0.15)',
          }
          : {
            // Same gradient-border recipe as WHITEBG (CustomButton.js:348-355).
            border: '1.5px solid transparent',
            backgroundImage: GRADIENT_BORDER_BG,
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            color: '#667085',
            fontWeight: 600,
          }),
      }}
    >
      {icon}
    </Box>
  );

  // ── step 1: select files ───────────────────────────────────────────────
  const renderStep1 = () => (
    <>
      <Box
        component={hasFiles ? 'div' : 'label'}
        onDrop={!hasFiles ? (e) => { e.preventDefault(); setDragOver(false); handleFileSelect(e.dataTransfer.files); } : undefined}
        onDragOver={!hasFiles ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
        onDragLeave={!hasFiles ? () => setDragOver(false) : undefined}
        sx={{
          display: 'block',
          border: hasFiles ? '1.5px solid transparent' : `2px dashed ${dragOver ? c.b300 : c.b200}`,
          borderRadius: hasFiles ? '16px' : '12px',
          p: { xs: '1.5rem 1rem', sm: '2rem 1.5rem' },
          textAlign: 'center',
          cursor: hasFiles ? 'default' : 'pointer',
          backgroundColor: hasFiles ? '#FFFFFF' : dragOver ? c.b50 : '#FFFFFF',
          ...(hasFiles
            ? {
              // Gradient border, same recipe as WHITEBG (CustomButton.js:348-355).
              backgroundImage: GRADIENT_BORDER_BG,
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
            }
            : {}),
          mb: 2.5,
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
        }}
      >
        <Box sx={{
          width: { xs: 44, sm: 52 }, height: { xs: 44, sm: 52 },
          borderRadius: hasFiles ? '50%' : '14px',
          backgroundColor: c.whiteBG, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5,
          boxShadow: 'none',
          // Gradient border, same recipe as WHITEBG (CustomButton.js:348-355). The inner
          // layer is white rather than truly transparent - that is what makes the ring
          // possible - and it reads as transparent against the white card behind it.
          border: '1.5px solid transparent',
          backgroundImage: GRADIENT_BORDER_BG,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
        }}>
          {hasFiles ? <GradientIcon icon={Check} size={24} /> : <GradientIcon icon={Upload} size={24} />}
        </Box>

        {hasFiles ? (
          <>
            <Typography sx={{ fontWeight: 700, color: '#101828', fontSize: { xs: '0.95rem', sm: '1rem' }, mb: 0.5 }}>
              {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''} Ready
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, color: '#667085', mb: 1.5, lineHeight: 1.5 }}>
              Ready for AI analysis & scoring
            </Typography>
            {/* Secondary's own padding (12px block) renders ~47px - too close to the
                Continue CTA. Size overrides only; border/colour stay the component's. */}
            <Secondary component="label" size="small" sx={{ height: 32, minWidth: 0, padding: '0 14px', fontSize: '0.8rem' }}>
              Change Files
              <VisuallyHiddenInput type="file" accept="application/pdf" multiple onChange={(e) => { setSelectedFiles([]); handleFileSelect(e.target.files); }} />
            </Secondary>
          </>
        ) : (
          <>
            <Typography sx={{ fontWeight: 700, color: c.b300, fontSize: { xs: '0.95rem', sm: '1rem' }, mb: 0.5 }}>
              Select Pitch Decks
            </Typography>
            <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' }, color: c.g200, lineHeight: 1.5 }}>
              Drag & drop or click — PDF files up to 50MB, max 50 files
            </Typography>
            <VisuallyHiddenInput type="file" accept="application/pdf" multiple onChange={(e) => handleFileSelect(e.target.files)} />
          </>
        )}
      </Box>

      <Primary
        onClick={hasFiles ? () => setWizardStep(1) : undefined}
        component={hasFiles ? 'button' : 'label'}
        fullWidth
        endIcon={hasFiles ? <ArrowForwardIcon /> : <></>}
        sx={{ height: { xs: '44px', sm: '48px' }, fontSize: { xs: '0.875rem', sm: '0.95rem' }, fontWeight: 600, borderRadius: '10px', mb: 0.75, color: c.whiteBG }}
      >
        {hasFiles ? 'Continue' : 'Upload Pitch Decks'}
        {!hasFiles && <VisuallyHiddenInput type="file" accept="application/pdf" multiple onChange={(e) => handleFileSelect(e.target.files)} />}
      </Primary>
    </>
  );

  // ── step 2: investment thesis ──────────────────────────────────────────
  const renderStep2 = () => (
    <>
      {isUploading && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 0.75, sm: 1 }, px: { xs: 0.75, sm: 1.5 }, py: { xs: 1, sm: 1.25 }, mb: 2, backgroundColor: '#fffbeb', border: '1px solid #FCD34D', borderRadius: '8px' }}>
          <InfoOutlinedIcon sx={{ fontSize: { xs: 13, sm: 17 }, color: '#D97706', mt: '2px', flexShrink: 0 }} />
          <Typography sx={{ fontSize: { xs: '0.7rem', sm: '0.82rem' }, color: '#92400E', lineHeight: 1.4 }}>
            Do not refresh or close this page while files are uploading.
          </Typography>
        </Box>
      )}

      <Box sx={isUploading ? { pointerEvents: 'none', opacity: 0.5, userSelect: 'none' } : {}}>
        {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <SparkleIcon sx={{ fontSize: 18, color: c.b300 }} />
          <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: c.b300 }}>Investment Thesis</Typography>
          <Tooltip title="Define your criteria to score and rank pitch decks" placement="top" arrow>
            <InfoOutlinedIcon sx={{ fontSize: 17, color: c.g100 }} />
          </Tooltip>
        </Box> */}

        <Grid container spacing={{ xs: 0.75, sm: 1.5 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CustomLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Stage</CustomLabel>
              <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.825rem' }, color: c.r300, fontWeight: 600 }}>*</Typography>
            </Box>
            <Autocomplete {...multiSelectProps('stage', stages?.map((s) => ({ label: s.name, value: s?._id, _id: s?._id })) || [], 'Stage')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CustomLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Category</CustomLabel>
            <Autocomplete
              {...multiSelectProps('category', categories?.map((cat) => ({ label: cat.name, value: cat?._id, _id: cat?._id })) || [], 'Category')}
              onChange={(_, newVal) => setConfig({ selectedCategories: newVal.map((o) => o.label).join(','), category: newVal?.map((item) => item?.value)?.join(','), sector: '' })}
            />
          </Grid>
          {shortlistConfig.category && (
            <Grid item xs={12} sm={6}>
              <CustomLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Sector</CustomLabel>
              <Autocomplete {...multiSelectProps('sector', subCategories?.map((s) => ({ label: s.sub_category, value: s?._id, _id: s?._id })) || [], 'Sector')} />
            </Grid>
          )}
          <Grid item xs={12} sm={shortlistConfig.category ? 6 : 12}>
            <CustomLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Business Model</CustomLabel>
            <Autocomplete {...multiSelectProps('business_model', businessModels?.map((s) => ({ label: s.name, value: s?._id, _id: s?._id })) || [], 'Business Model')} />
          </Grid>
          {/* <GeoMultiSelect
            labels={{ country: 'Country', state: 'State', city: 'City' }}
            countryIds={shortlistConfig.country ? shortlistConfig.country.split(',').filter(Boolean) : []}
            stateIds={shortlistConfig.state ? shortlistConfig.state.split(',').filter(Boolean) : []}
            cityIds={shortlistConfig.city ? shortlistConfig.city.split(',').filter(Boolean) : []}
            onChange={({ countryIds, stateIds, cityIds }) => {
              setShortlistConfig((prev) => ({
                ...prev,
                country: (countryIds || []).join(','),
                state: (stateIds || []).join(','),
                city: (cityIds || []).join(','),
              }));
            }}
          /> */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 0.75 }}>
              <CustomLabel sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Startup Criteria</CustomLabel>
              <Tooltip title="Describe what makes an ideal startup for your portfolio" placement="top" arrow>
                <InfoOutlinedIcon sx={{ fontSize: { xs: 13, sm: 16 }, color: c.g100, mb: '6px', cursor: 'help', flexShrink: 0 }} />
              </Tooltip>
            </Box>
            <TextField
              fullWidth multiline rows={3} variant="outlined"
              placeholder="Example: Strong team background, clear market opportunity with high TAM, solid unit economics..."
              value={shortlistConfig.startup_criteria}
              onChange={(e) => { if (e.target.value.length <= 1000) setConfig({ startup_criteria: e.target.value }); }}
              sx={{
                marginBottom: 0,
                "& > .MuiInputBase-root > textarea": { padding: "0.1rem !important" },
                '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: { xs: '0.75rem', sm: '0.875rem' } },
                '& .MuiInputBase-input': { padding: { xs: '0.65rem', sm: '0.75rem' } },
                '& .MuiInputBase-input::placeholder': { color: c.g100, opacity: 1 },
                '& fieldset.MuiOutlinedInput-notchedOutline': { borderColor: c.b50 + "!important" }
              }}
            />
            <Typography sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' }, color: c.g100, textAlign: 'right', mt: 0.5 }}>
              {shortlistConfig.startup_criteria.length}/1000
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, mt: 2.5 }}>
        {/* <WHITEBG
          onClick={() => setWizardStep(0)}
          disabled={isUploading}
          sx={{ height: '48px', borderRadius: '10px', flex: '0 0 auto', px: 2, border: `1px solid ${c.b200}`, color: c.g200, fontWeight: 600 }}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </WHITEBG> */}
        <Primary
          onClick={handleUpload}
          fullWidth
          disabled={isUploading || !shortlistConfig.stage}
          sx={{ height: { xs: '44px', sm: '48px' }, fontSize: { xs: '0.875rem', sm: '0.95rem' }, fontWeight: 600, borderRadius: '10px', color: c.whiteBG, ':disabled': { color: c.whiteBG } }}
          startIcon={isUploading ? <CircularProgress size={18} sx={{ color: c.whiteBG }} /> : <SparkleIcon />}
        >
          {isUploading ? 'Uploading...' : `Analyze ${selectedFiles.length} File${selectedFiles.length !== 1 ? 's' : ''}`}
        </Primary>
      </Box>
    </>
  );

  if (isDataLoading) {
    return <BulkAnalysisStartSkeleton />;
  }

  return (
    <ThemeBGWrapper>
      <BreadcrumbHeader
        title="Start"
      />

      <PerfectScrollbar containerRef={containerRef} style={{ height: 'calc(100% - 5rem)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: "flex-start", px: { xs: 0.75, sm: 2 }, py: { xs: 2, sm: 4 }, minHeight: 'calc(100vh - 140px)' }}>
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', sm: 520 },
              backgroundColor: c.whiteBG,
              borderRadius: { xs: '16px', sm: '28px' },
              boxShadow: '0 20px 50px 0 rgba(51, 51, 77, 0.10)',
              p: { xs: '1rem', sm: '1.5rem', md: '2rem' },
              mx: { xs: '0.75rem', sm: 0 },
            }}
          >
            {/* Stepper */}
            <Stepper activeStep={wizardStep} alternativeLabel sx={{ mb: { xs: 1.5, sm: 3 } }}>
              {STEPS.map((label) => (
                <Step key={label}>
                  <StepLabel
                    StepIconComponent={StepCircle}
                    sx={{
                      '& .MuiStepLabel-label': { fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 600, mt: 0.5, color: '#667085' },
                      '& .MuiStepLabel-label.Mui-active': { color: '#101828' },
                      '& .MuiStepLabel-label.Mui-completed': { color: '#101828' },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step content */}
            {wizardStep === 0 ? renderStep1() : renderStep2()}

            {/* Footer */}
            <AssessmentFooter />
          </Box>
        </Box>
      </PerfectScrollbar>
    </ThemeBGWrapper>
  );
};

export default BulkAnalysisStartPage;
